/**
 * Pure kalkyler for Feilbudsjettering-widgetene (VIB-1319).
 *
 * Avvik-konvensjonen er Bonusmål-fasiten (docs/kpi-source-of-truth.md,
 * variant B — timerbasert):
 *
 *   avvik (%) = (brukte − solgte timer) / solgte timer × 100
 *
 * Negativt = under budsjett = bra. Alle aggregat er VEKTEDE totaler
 * (Σ brukt − Σ solgt) / Σ solgt — aldri «snitt av snitt» (Erik-avklaring Å2,
 * plan 2026-07-07). Teamsnittet over «År»-perioden er dermed identisk med
 * Bonusmål-widgetens budsjettavvik-tall.
 *
 * Team-basis (teamsnitt): solgte timer = budget_lines.quantity_hours på
 * interne linjer, brukte timer = alle time_entries på prosjektet — samme
 * datagrunnlag som use-bonus-goals.ts.
 * Per-ansatt-basis: ansattes egne assignments (budget_line_assignments.hours)
 * mot ansattes egne loggede timer på prosjektet (Erik-avklaring Å3).
 */

export type DeviationPeriod = 'maned' | 'kvartal' | 'ar';

/** Periode-valg for SegmentedPill i begge widgets. */
export const DEVIATION_PERIOD_OPTIONS: { value: DeviationPeriod; label: string }[] = [
  { value: 'maned', label: 'Måned' },
  { value: 'kvartal', label: 'Kvartal' },
  { value: 'ar', label: 'År' },
];

export interface DeviationProject {
  id: string;
  name: string;
  projectCode: string | null;
  customerName: string | null;
  /** ISO-timestamp for ferdigstillelse (project_status=completed). */
  completedAt: string;
  /** Team-basis: Σ budget_lines.quantity_hours (interne linjer). */
  teamSoldHours: number;
  /** Team-basis: Σ time_entries (alle brukere) i timer. */
  teamUsedHours: number;
}

export interface EmployeeProjectHours {
  userId: string;
  projectId: string;
  /** Ansattes egne tildelte timer (assignments, ikke-eksterne linjer). */
  assignedHours: number;
  /** Ansattes egne loggede timer på prosjektet. */
  loggedHours: number;
}

export interface DeviationUser {
  userId: string;
  fullName: string;
  role: string;
}

export interface PeriodWindow {
  /** Inklusiv ISO-dato (YYYY-MM-DD). */
  start: string;
  /** Eksklusiv ISO-dato (YYYY-MM-DD). */
  end: string;
  /** Visningsnavn, f.eks. «Juni 2026», «Q2 2026», «Hittil i 2026». */
  name: string;
}

export interface TeamDeviation {
  /** Vektet avvik i %, null når ingen solgte timer i perioden. */
  pct: number | null;
  totalSoldHours: number;
  totalUsedHours: number;
  projectCount: number;
}

export interface EmployeeDeviationRow {
  userId: string;
  fullName: string;
  role: string;
  projectCount: number;
  assignedHours: number;
  loggedHours: number;
  /** Vektet avvik i % over egne assignments. */
  pct: number;
}

export interface OwnProjectRow {
  projectId: string;
  name: string;
  projectCode: string | null;
  customerName: string | null;
  completedAt: string;
  assignedHours: number;
  loggedHours: number;
  pct: number;
}

const MONTH_FULL = ['', 'Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Kjerneformelen. Null når solgte timer mangler (avvik udefinert). */
export function deviationPct(usedHours: number, soldHours: number): number | null {
  if (soldHours <= 0) return null;
  return ((usedHours - soldHours) / soldHours) * 100;
}

/** Signert prosent med norsk komma og ekte minustegn: «−9,8 %» / «+11,4 %». */
export function signedPct(value: number, decimals = 1): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return sign + Math.abs(value).toFixed(decimals).replace('.', ',') + ' %';
}

/** Periodevindu for Måned/Kvartal/År relativt til `now`. */
export function resolvePeriod(period: DeviationPeriod, now: Date): PeriodWindow {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (period === 'maned') {
    const nextY = month === 12 ? year + 1 : year;
    const nextM = month === 12 ? 1 : month + 1;
    return {
      start: isoDate(year, month, 1),
      end: isoDate(nextY, nextM, 1),
      name: `${MONTH_FULL[month]} ${year}`,
    };
  }
  if (period === 'kvartal') {
    const q = Math.floor((month - 1) / 3) + 1;
    const startM = (q - 1) * 3 + 1;
    const endM = startM + 3;
    return {
      start: isoDate(year, startM, 1),
      end: endM > 12 ? isoDate(year + 1, 1, 1) : isoDate(year, endM, 1),
      name: `Q${q} ${year}`,
    };
  }
  return {
    start: isoDate(year, 1, 1),
    end: isoDate(year + 1, 1, 1),
    name: `Hittil i ${year}`,
  };
}

/** Prosjekter ferdigstilt innenfor vinduet (completed_at ∈ [start, end)). */
export function filterProjectsInPeriod<T extends { completedAt: string }>(
  projects: T[],
  window: PeriodWindow,
): T[] {
  return projects.filter((p) => p.completedAt >= window.start && p.completedAt < window.end);
}

/**
 * Teamets vektede avvik over ferdigstilte prosjekter i perioden.
 * Over «År»-vinduet gir dette samme tall som Bonusmål-widgeten.
 */
export function computeTeamDeviation(projects: DeviationProject[]): TeamDeviation {
  const totalSoldHours = projects.reduce((s, p) => s + p.teamSoldHours, 0);
  const totalUsedHours = projects.reduce((s, p) => s + p.teamUsedHours, 0);
  return {
    pct: deviationPct(totalUsedHours, totalSoldHours),
    totalSoldHours,
    totalUsedHours,
    projectCount: projects.length,
  };
}

/**
 * Vektet avvik per ansatt over egne assignments på prosjektene i perioden,
 * sortert best → dårligst (mest negative først). Ansatte uten tildelte timer
 * i perioden utelates (avvik udefinert).
 */
export function computeEmployeeDeviations(
  employeeHours: EmployeeProjectHours[],
  projectIds: ReadonlySet<string>,
  users: DeviationUser[],
): EmployeeDeviationRow[] {
  const byUser = new Map<string, { assigned: number; logged: number; projects: Set<string> }>();
  for (const eh of employeeHours) {
    if (!projectIds.has(eh.projectId)) continue;
    let agg = byUser.get(eh.userId);
    if (!agg) {
      agg = { assigned: 0, logged: 0, projects: new Set() };
      byUser.set(eh.userId, agg);
    }
    agg.assigned += eh.assignedHours;
    agg.logged += eh.loggedHours;
    if (eh.assignedHours > 0) agg.projects.add(eh.projectId);
  }

  const rows: EmployeeDeviationRow[] = [];
  for (const u of users) {
    const agg = byUser.get(u.userId);
    if (!agg) continue;
    const pct = deviationPct(agg.logged, agg.assigned);
    if (pct == null) continue;
    rows.push({
      userId: u.userId,
      fullName: u.fullName,
      role: u.role,
      projectCount: agg.projects.size,
      assignedHours: agg.assigned,
      loggedHours: agg.logged,
      pct,
    });
  }
  return rows.sort((a, b) => a.pct - b.pct);
}

/**
 * Egne prosjektrader (widget 6): ansattes tildelte vs. loggede timer per
 * ferdigstilt prosjekt i perioden. Prosjekter uten egne tildelte timer
 * utelates. Sortert på ferdigstillelsesdato stigende (som prototypen).
 */
export function computeOwnProjects(
  employeeHours: EmployeeProjectHours[],
  projects: DeviationProject[],
  userId: string,
): OwnProjectRow[] {
  const projectById = new Map(projects.map((p) => [p.id, p]));
  const rows: OwnProjectRow[] = [];
  for (const eh of employeeHours) {
    if (eh.userId !== userId || eh.assignedHours <= 0) continue;
    const project = projectById.get(eh.projectId);
    if (!project) continue;
    const pct = deviationPct(eh.loggedHours, eh.assignedHours);
    if (pct == null) continue;
    rows.push({
      projectId: project.id,
      name: project.name,
      projectCode: project.projectCode,
      customerName: project.customerName,
      completedAt: project.completedAt,
      assignedHours: eh.assignedHours,
      loggedHours: eh.loggedHours,
      pct,
    });
  }
  return rows.sort((a, b) => a.completedAt.localeCompare(b.completedAt));
}

/**
 * Symmetrisk skala for diverging bars: minst ±15 %, ellers auto-fit opp til
 * nærmeste 5-er som rommer største absoluttverdi (plan-beslutning: ±15 % med
 * symmetrisk auto-fit).
 */
export function divergingScaleMax(values: number[]): number {
  const maxAbs = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  return Math.max(15, Math.ceil(maxAbs / 5) * 5);
}

/** Posisjon (0–100 %) for en verdi på den symmetriske −max…+max-skalaen. */
export function divergingPositionPct(value: number, scaleMax: number): number {
  const clamped = Math.max(-scaleMax, Math.min(scaleMax, value));
  return ((clamped + scaleMax) / (2 * scaleMax)) * 100;
}

export interface DivergingBarGeometry {
  leftPct: number;
  widthPct: number;
  negative: boolean;
}

/** Segment-geometri fra 0-linjen til verdien (min. 0,4 % bredde for synlighet). */
export function divergingBarGeometry(value: number, scaleMax: number): DivergingBarGeometry {
  const p = divergingPositionPct(value, scaleMax);
  const z = divergingPositionPct(0, scaleMax);
  const left = Math.min(p, z);
  const width = Math.max(Math.max(p, z) - left, 0.4);
  return { leftPct: left, widthPct: width, negative: value <= 0 };
}
